import { ElementType, parseDocument } from 'htmlparser2';

// The allow-list for post and message content, and the pass that enforces it.
//
// It works on the htmlparser2 tree the renderer already builds, so the server
// render and the browser run this same code and cannot disagree. It needs no
// DOM, which is why the first HTML response is sanitised too. DOMPurify did
// this job before, but only in the browser: it needs a `window`.
//
// The rules are the ones DOMPurify 3.4 applied with this app's configuration
// (its defaults included), so nothing renders now that the browser refused
// before. Where the two differ, this is the stricter one, and each such place
// is marked below.

type ContentNode = ReturnType<typeof parseDocument>['children'][number];
type ContentElement = Extract<ContentNode, { attribs: unknown }>;

/** Tags that render. Anything else is removed; see `DROPPED_WITH_CONTENT`. */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'mark',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'blockquote',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  // Custom app tags
  'url',
  'mention',
  's',
  'f',
  'c'
]);

// A tag that is not allowed is removed and what it wrapped is kept, except for
// these, which go with everything inside them. The list is DOMPurify's default
// FORBID_CONTENTS.
const DROPPED_WITH_CONTENT: ReadonlySet<string> = new Set([
  'annotation-xml',
  'audio',
  'colgroup',
  'desc',
  'foreignobject',
  'head',
  'iframe',
  'math',
  'mi',
  'mn',
  'mo',
  'ms',
  'mtext',
  'noembed',
  'noframes',
  'noscript',
  'plaintext',
  'script',
  'selectedcontent',
  'style',
  'svg',
  'template',
  'thead',
  'title',
  'video',
  'xmp'
]);

/** Attributes any standard tag may carry. `style` and `data-*` are not here. */
const ALLOWED_ATTRIBUTES: ReadonlySet<string> = new Set([
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'title',
  'class'
]);

// The entity tags carry only what the renderer reads from them. Names are
// lowercase because the parser lowercases them: the stored `factiiiId` arrives
// as `factiiiid`. (Stricter than DOMPurify, which let any allowed attribute sit
// on these tags.)
const ENTITY_ATTRIBUTES: Readonly<Record<string, ReadonlySet<string>>> = {
  url: new Set(['id']),
  mention: new Set(['id']),
  c: new Set(['id']),
  f: new Set(['id', 'factiiiid'])
};

// `<s id>` and `<code id>` are entity tags too, but `<s>` and `<code>` are
// also plain HTML, so they keep the standard attributes as well.
//
// No other tag keeps an `id`. The renderer reads it off an entity tag and never
// writes it to the page, so content cannot put an id into the document at all.
// (Stricter than DOMPurify, which dropped only an id that shadowed a property
// of `document`, a check that needs a DOM.)
const ID_TAGS: ReadonlySet<string> = new Set(['s', 'code']);

const ARIA_ATTRIBUTE = /^aria-[-\w]+$/;

// The number an ordered list counts from. The markdown tagger writes it for a
// list typed from another number than 1 (`3. third point`).
const LIST_START = /^\d{1,9}$/;

// Attributes whose value is never a URL, so it is not checked as one.
const TEXT_ATTRIBUTES: ReadonlySet<string> = new Set(['alt', 'class', 'title']);

// DOMPurify's default IS_ALLOWED_URI: a known scheme, or a value with no scheme
// at all (a path, a fragment, a bare word). Tested with whitespace and control
// characters removed, so `java\tscript:` is still seen as a scheme.
const ALLOWED_URI =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;
const URI_WHITESPACE =
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;
// Only an image may load a `data:` URL.
const DATA_URI_TAGS: ReadonlySet<string> = new Set(['img']);
// A value that could close a tag or a comment if the markup were ever written
// back out as a string. DOMPurify drops the attribute; so does this.
const BREAKS_OUT_OF_MARKUP =
  /((--!?|])>)|<\/(style|script|title|xmp|textarea|noscript|iframe|noembed|noframes)|\/>/i;
const FACTIII_ID = /^\d+$/;

const isElement = (node: ContentNode): node is ContentElement =>
  'attribs' in node;

/** Whether `tag` may keep the attribute `name` with this (trimmed) value. */
export const isAllowedAttribute = (
  tag: string,
  name: string,
  value: string
): boolean => {
  if (BREAKS_OUT_OF_MARKUP.test(value)) return false;

  const entityAttributes = ENTITY_ATTRIBUTES[tag];
  if (name === 'id') {
    return entityAttributes?.has('id') === true || ID_TAGS.has(tag);
  }
  if (entityAttributes) {
    // Stricter than DOMPurify: the id becomes a path segment, so digits only.
    return entityAttributes.has(name) && FACTIII_ID.test(value);
  }
  if (ARIA_ATTRIBUTE.test(name)) return true;
  if (tag === 'ol' && name === 'start') return LIST_START.test(value);
  if (!ALLOWED_ATTRIBUTES.has(name)) return false;
  if (TEXT_ATTRIBUTES.has(name)) return true;

  // Everything left is checked as a URL — `target` and `rel` as well, which is
  // what keeps a `javascript:` value out of them.
  if (ALLOWED_URI.test(value.replace(URI_WHITESPACE, ''))) return true;
  if (
    (name === 'src' || name === 'href') &&
    value.startsWith('data:') &&
    DATA_URI_TAGS.has(tag)
  ) {
    return true;
  }
  return value === '';
};

const sanitizeAttributes = (element: ContentElement) => {
  const { attribs, name: tag } = element;
  for (const name of Object.keys(attribs)) {
    const value = attribs[name].trim();
    if (isAllowedAttribute(tag, name.toLowerCase(), value)) {
      attribs[name] = value;
    } else {
      delete attribs[name];
    }
  }
};

const isBlank = (node: ContentNode) =>
  node.type === ElementType.Text && node.data.trim() === '';

// A browser reading `<table><tr>` puts the rows in a <tbody> of its own. React
// does not, so the markup the server sent and the tree React expects on the
// client would differ — a hydration error. Adding the <tbody> here means both
// sides start from what the browser will build anyway.
const wrapStrayRows = (children: ContentNode[]): ContentNode[] => {
  const wrapped: ContentNode[] = [];
  let body: ContentElement | null = null;
  for (const child of children) {
    const isRow = isElement(child) && child.name === 'tr';
    if (isRow || (body && isBlank(child))) {
      if (!body) {
        body = parseDocument('<tbody></tbody>').children[0] as ContentElement;
        wrapped.push(body);
      }
      body.children.push(child);
    } else {
      body = null;
      wrapped.push(child);
    }
  }
  for (const child of wrapped) {
    if (isElement(child) && child.name === 'tbody') adopt(child);
  }
  return wrapped;
};

// Re-link a parent to the children it ended up with, since the renderer walks
// `parent` and the sibling pointers.
const adopt = (parent: { children: ContentNode[] }) => {
  const { children } = parent;
  children.forEach((child, index) => {
    child.parent = parent as ContentNode['parent'];
    child.prev = children[index - 1] ?? null;
    child.next = children[index + 1] ?? null;
  });
};

const sanitizeNodes = (nodes: ContentNode[]): ContentNode[] => {
  const kept: ContentNode[] = [];
  for (const node of nodes) {
    if (node.type === ElementType.Text) {
      kept.push(node);
      continue;
    }
    // Comments, doctypes, processing instructions and CDATA never render.
    if (!isElement(node)) continue;

    const tag = node.name.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      if (!DROPPED_WITH_CONTENT.has(tag)) {
        kept.push(...sanitizeNodes(node.children));
      }
      continue;
    }

    sanitizeAttributes(node);
    const children = sanitizeNodes(node.children);
    // An entity tag missing what makes it one (`<url>` with no id, `<f>` with
    // no factiii) is not a tag the renderer knows. Keep the text, not an
    // element the browser has never heard of.
    const required = ENTITY_ATTRIBUTES[tag];
    if (required && ![...required].every((name) => node.attribs[name])) {
      kept.push(...children);
      continue;
    }
    node.children = tag === 'table' ? wrapStrayRows(children) : children;
    adopt(node);
    kept.push(node);
  }
  return kept;
};

/**
 * Parse content and strip it to the allow-list. Returns the top-level nodes,
 * ready for `domToReact`. The same call runs on the server and in the browser.
 */
export const parseSanitizedContent = (html: string): ContentNode[] => {
  const document = parseDocument(html);
  document.children = sanitizeNodes(document.children);
  adopt(document);
  return document.children;
};
